import { sort } from 'timsort';

// Test if a value is a filter
export function isFilter(val) {
  if (!val || typeof val !== 'object') {
    return false;
  }

  if (val.id === 'custom' || val.name === 'All devices') {
    return true;
  } if (typeof val.id === 'string' && !val.alias) {
    return true;
  }
  return false;
}

// Test if a value is a property
export function isProperty(val) {
  if (!val || typeof val !== 'object') {
    return false;
  }

  if (val.name && val.alias) {
    return true;
  }

  return false;
}

// Test if a value is a property
export function isTime(val) {
  if (!val || typeof val !== 'object') {
    return false;
  }

  if (val.name && val.id && val.offset) {
    return true;
  }

  return false;
}

/**
 * Pass in an array of filters, or where clauses to get a combined query string
 *
 * @param {Array} filterArray
 */
export function combineFilters(filterArray) {
  let combined = null;

  filterArray.forEach((f) => {
    if (!f) return;
    if (combined !== null) {
      combined += ' && ';
    }

    let addition = '';
    if (typeof f === 'object' && f.expression) {
      addition = f.expression;
    } else {
      addition = f;
    }

    if (combined) {
      combined += `(${addition})`;
    } else {
      combined = `(${addition})`;
    }
  });

  return combined;
}

// Generic comparison function which checks filters, properties and primitives
export const valueEquals = (a, b) => {
  // Shortcut for native values to improve return speed
  if (typeof a !== 'object' && typeof b !== 'object') {
    return a === b;
  }

  if (isFilter(a) && isFilter(b)) {
    return a.id === b.id;
  } if (isProperty(a) && isProperty(b)) {
    return a.name === b.name;
  }
  return a === b;
};

export function isValidEmail(email) {
  if (!email || typeof email !== 'string') {
    return false;
  }

  const splits = email.split('@');
  if (splits.length === 2) {
    return true;
  }
  return false;
}

// A simple helper function to filter a property based on its fields against a text string
export function searchProperty(property, text) {
  if (!text) {
    return true;
  }
  const lowerText = `${text}`.toLowerCase();
  const searchableText = `${property.name} ${property.alias}`.toLowerCase();
  return searchableText.indexOf(lowerText) > -1;
}

export function searchValues(searchObject, searchString, fields = []) {
  if (!searchString) return true;
  const filter = `${searchString}`.toLowerCase();
  const fieldsToSearch = fields.length === 0 ? Object.keys(searchObject) : fields;
  return fieldsToSearch
    .map((k) => `${searchObject[k]}`.toLowerCase())
    .some((v) => v.includes(filter));
}

/**
 * Simple function to iterate through an object with an array of keys to find deep values
 *
 * @param {Object} obj
 * @param {Array} keys
 */
export function getDeepObjectValue(obj, keys = []) {
  let value = obj;
  for (let i = 0; i < keys.length; i += 1) {
    value = value[keys[i]];
  }
  return value;
}

// Natural sorting function
const sortFunc = (a, b, asc) => {
  const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

  if (a === b) {
    return 0;
  }
  if (a === null || a === undefined || a === '') {
    return 1;
  }
  if (b === null || b === undefined || b === '') {
    return -1;
  }

  if (typeof a === 'number' && typeof b === 'number') {
    return asc ? b - a : a - b;
  }

  return asc ? collator.compare(b, a) : collator.compare(a, b);
};

/**
 * Sort data in a natural way (so numbers get sorted correctly), using timsort which works better
 * than the default JS sort for larger datasets
 *
 * @param {Array} data The array of data to be sorted
 * @param {String*} key Optional key to allow sorting of objects
 */
export function naturalSort(data, key, dataFormat) {
  if (!data) return [];
  const returnedData = [...data];

  if (data.length === 0) {
    return returnedData;
  }

  // Sort the data with the optional key
  const sFn = (a, b) => {
    let aVal = a;
    let bVal = b;
    if (key && typeof key === 'string') {
      aVal = a[key];
      bVal = b[key];
    } else if (key && Array.isArray(key)) {
      aVal = getDeepObjectValue(a, key);
      bVal = getDeepObjectValue(b, key);
    } else if (key && typeof key === 'function') {
      aVal = key(a);
      bVal = key(b);
    }

    if (dataFormat === 'moment') {
      aVal = aVal && typeof aVal === 'object' ? aVal.valueOf() : aVal;
      bVal = bVal && typeof bVal === 'object' ? bVal.valueOf() : bVal;
    }

    return sortFunc(aVal, bVal);
  };

  try {
    sort(returnedData, sFn);
  } catch (__) {
    // timsort can fail: https://github.com/mziccard/node-timsort/issues/14
    returnedData.sort(sortFunc);
  }

  return returnedData;
}

/**
 * Reverse natural sorting. Just runs naturalSort() and then uses JS Array.reverse()
 *
 * @param {Array} data The array of data to be sorted
 * @param {String*} key Optional key to allow sorting of objects
 */
export function reverseNaturalSort(data, key, dataFormat) {
  if (!data) return [];
  const returnedData = [...data];

  if (data.length === 0) {
    return returnedData;
  }

  const sFn = (a, b) => {
    let aVal = a;
    let bVal = b;
    if (key && typeof key === 'string') {
      aVal = a[key];
      bVal = b[key];
    } else if (key && Array.isArray(key)) {
      aVal = getDeepObjectValue(a, key);
      bVal = getDeepObjectValue(b, key);
    }

    if (dataFormat === 'moment') {
      aVal = aVal && typeof aVal === 'object' ? aVal.valueOf() : aVal;
      bVal = bVal && typeof bVal === 'object' ? bVal.valueOf() : bVal;

      if (aVal === bVal) {
        return 0;
      }
      if (aVal === '' || !aVal) {
        return 1;
      }
      if (bVal === '' || !bVal) {
        return -1;
      }
    }
    return sortFunc(aVal, bVal, true);
  };

  try {
    // Sort the data with the optional key
    sort(returnedData, sFn);
  } catch (__) {
    // timsort can fail: https://github.com/mziccard/node-timsort/issues/14
    returnedData.sort(sortFunc);
  }

  return returnedData;
}

export function escapeTags(str) {
  if (!str) return str;
  return `${str}`.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function isLink(text) {
  return /(https?:\/\/[^\s]+)/g.test(text);
}

/**
 * Turns a string into an HTML string if it where links get converted into <a> tags. If there are no
 * links then the string is just returned however it is passed in
 *
 * @param {String} str Text to linkify
 */
export function linkify(str) {
  if (!str || typeof str !== 'string') {
    return str;
  }

  function addProtocol(url) {
    if (/(([\w]+:)?\/\/)/ig.test(url) === false) {
      return `http://${url}`;
    }
    return url;
  }

  const parsedStr = encodeURI(str);
  let isUrl = false;

  const text = parsedStr.replace(/[http?s](([\w]+:)?\/\/)(([\d\w]|%[a-fA-f\d]{2,2})+(:([\d\w]|%[a-fA-f\d]{2,2})+)?@)?([\d\w][-\d\w]{0,253}[\d\w]\.)+[\w]{2,4}(:[\d]+)?(\/([-+_~.\d\w]|%[a-fA-f\d]{2,2})*)*(\?(&?([-+_~.\d\w]|%[a-fA-f\d]{2,2})=?)*)?(#([-+_~.\d\w\/]|%[a-fA-f\d]{2,2})*)?/ig, (url) => { // eslint-disable-line no-useless-escape
    isUrl = true;
    const wrap = document.createElement('div');
    const anchor = document.createElement('a');
    anchor.href = addProtocol(url);
    anchor.target = '_blank';
    anchor.innerHTML = url;
    wrap.appendChild(anchor);
    return wrap.innerHTML;
  });

  if (!text) {
    return '';
  }

  return isUrl ? text : str;
}

export function saveFile(blob, filename) {
  if (window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, filename);
  } else {
    const a = document.createElement('a');
    document.body.appendChild(a);
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }, 0);
  }
}

export function clearTextSelection() {
  if (window.getSelection) {
    if (window.getSelection().empty) { // Chrome
      window.getSelection().empty();
    } else if (window.getSelection().removeAllRanges) { // Firefox
      window.getSelection().removeAllRanges();
    }
  } else if (document.selection) { // IE?
    document.selection.empty();
  }
}

/**
 * A little helper class to wrap "cancel"-able Promises. As we cannot actually cancel a promise
 * this helper will just resolve to the results of the last called promise. This class should be
 * instantiated in a component's "data" and then called by using `.push` to add a new promise and
 * then `.runMostRecent` with the same promise, which will only run if that promise is current
 */
export class PromiseTracker {
  constructor() {
    this.lastPromise = null;
  }

  /**
   * Adds a new, most-recent Promise
   * @param {Promise} newPromise
   */
  push(newPromise) {
    this.lastPromise = newPromise;
  }

  flush() {
    this.lastPromise = null;
  }

  /**
   * Simple comparison to check promise equality
   * @param {Promise} promise
   */
  isPromiseCurrent(promise) {
    return promise === this.lastPromise;
  }

  /**
   * Will resolve (or reject) the most recently added promise and will ignore any others
   * @param {Promise} testPromise
   */
  async runMostRecent(testPromise) {
    return new Promise((resolve, reject) => {
      testPromise.then((results) => {
        if (!this.isPromiseCurrent(testPromise)) {
          return;
        }
        this.lastPromise = null;
        resolve(results);
      }).catch((err) => {
        if (!this.isPromiseCurrent(testPromise)) {
          return;
        }
        this.lastPromise = null;
        reject(err);
      });
    });
  }
}

export function binSizeToMs(binSize) {
  const binSizes = {
    '1m': 60000,
    '5m': 300000,
    '15m': 900000,
    '30m': 1800000,
    '1h': 3600000,
    '3h': 10800000,
    '6h': 21600000,
    '12h': 43200000,
    '1d': 86400000,
    '3d': 259200000,
    '1w': 604800000,
    '2w': 1210000000,
    '4w': 2419000000,
    '1M': 2592000000,
  };

  return binSizes[binSize];
}

export function toMinTimePeriod(ms) {
  const oneDay = 86400000;

  return ms < oneDay ? oneDay : ms;
}

// We should improve this guessing function before 2033
export function guessIfDate(value) {
  const numberVersion = typeof value === 'number' ? value : Number.parseInt(value, 10);
  return `${numberVersion}`.startsWith('1') && `${numberVersion}`.length === 13;
}
